import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Chip, CircularProgress, Alert,
} from '@mui/material';
import { Send, CheckCircle } from '@mui/icons-material';
import apiClient from '../../services/api/apiClient';

interface ErrorReportDialogProps {
  open: boolean;
  onClose: () => void;
  errorType: '404' | 'runtime' | 'api';
  errorMessage: string;
  errorStack?: string;
  componentStack?: string;
  errorPath?: string;
}

export const ErrorReportDialog: React.FC<ErrorReportDialogProps> = ({
  open, onClose, errorType, errorMessage, errorStack, componentStack, errorPath,
}) => {
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const collectEnvironmentInfo = () => ({
    url: window.location.href,
    path: errorPath || window.location.pathname,
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    timestamp: new Date().toISOString(),
    referrer: document.referrer || '(direct)',
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      const envInfo = collectEnvironmentInfo();

      await apiClient.post('/error-report', {
        errorType,
        errorMessage,
        errorStack: errorStack?.slice(0, 2000),
        componentStack: componentStack?.slice(0, 1000),
        description: description.trim(),
        environment: envInfo,
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setDescription('');
        onClose();
      }, 2000);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || '리포트 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setDescription('');
      setSubmitError('');
      setSubmitted(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        오류 리포트
      </DialogTitle>

      <DialogContent>
        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 60, color: 'var(--ws-green)', mb: 2 }} />
            <Typography variant="h6" fontWeight={600}>
              리포트가 전송되었습니다
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              개발팀이 확인 후 조치하겠습니다. 감사합니다.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" sx={{ mb: 2, fontSize: 14 }}>
              발생한 오류에 대해 개발팀에 리포트를 보냅니다.
              어떤 상황에서 오류가 발생했는지 설명해 주시면 더 빠르게 해결할 수 있습니다.
            </Typography>

            {/* 오류 정보 요약 */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'var(--ws-subtle)', borderRadius: 2, border: '1px solid var(--ws-line)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={errorType === '404' ? '404 페이지 없음' : errorType === 'runtime' ? '런타임 오류' : 'API 오류'}
                  size="small"
                  color={errorType === '404' ? 'warning' : 'error'}
                />
                <Chip label={errorPath || window.location.pathname} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
              </Box>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ws-ink-3)', wordBreak: 'break-all' }}>
                {errorMessage.slice(0, 200)}
              </Typography>
            </Box>

            {/* 사용자 설명 입력 */}
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="어떤 작업을 하다가 이 오류가 발생했나요? (선택사항)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              sx={{ mb: 1 }}
            />

            <Typography variant="caption" color="text.secondary">
              브라우저 정보, 화면 크기, 접속 경로 등 환경 정보가 자동으로 포함됩니다.
            </Typography>

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>
            )}
          </>
        )}
      </DialogContent>

      {!submitted && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Send />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            {submitting ? '전송 중...' : '리포트 전송'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
